/**
 * Safe Focus Utility
 * 
 * Firefox Mobile has stricter focus handling than other browsers.
 * These utilities provide safe ways to manage focus that won't throw errors
 * when elements are not focusable, removed from DOM, or when blur/focus
 * methods don't exist.
 */

/**
 * Check if an element can safely receive focus operations
 * @param {Element} element - The element to check
 * @returns {boolean} - Whether the element can be focused/blurred
 */
export const isFocusable = (element) => {
    if (!element) return false
    if (element === document.body) return false
    if (typeof element.blur !== 'function') return false
    if (typeof element.focus !== 'function') return false
    
    // Check if element is still in the DOM
    if (!document.body.contains(element)) return false
    
    // Check if element is visible and not disabled
    if (element.disabled) return false
    if (element.hidden) return false
    
    return true
}

/**
 * Safely blur the currently focused element
 * Works around Firefox Mobile focus handling issues
 * @returns {Element|null} - The element that was blurred, or null if none
 */
export const safeBlur = () => {
    try {
        const activeElement = document.activeElement
        if (isFocusable(activeElement)) {
            activeElement.blur()
            return activeElement
        }
    } catch (err) {
        // Silently ignore focus errors - this is expected on Firefox Mobile
        // in certain edge cases (element removed, SVG elements, etc.)
    }
    return null
}

/**
 * Safely focus an element
 * Works around Firefox Mobile focus handling issues
 * @param {Element} element - The element to focus
 * @param {Object} options - Focus options (preventScroll, etc.)
 * @returns {boolean} - Whether the focus was successful
 */
export const safeFocus = (element, options = {}) => {
    try {
        if (!element) return false
        if (typeof element.focus !== 'function') return false
        
        // Check if element is still in the DOM
        if (!document.body.contains(element)) return false
        
        element.focus(options)
        return true
    } catch (err) {
        // Silently ignore focus errors
        return false
    }
}

/**
 * Safely blur and then optionally focus another element
 * Useful for modal/drawer close operations
 * @param {Element} elementToFocus - Optional element to focus after blur
 * @param {Object} options - Focus options
 * @returns {boolean} - Whether the operation was successful
 */
export const safeBlurAndFocus = (elementToFocus = null, options = {}) => {
    safeBlur()
    
    if (elementToFocus) {
        // Use requestAnimationFrame to ensure DOM has updated
        // This is especially important on Firefox Mobile
        return new Promise((resolve) => {
            requestAnimationFrame(() => {
                const result = safeFocus(elementToFocus, options)
                resolve(result)
            })
        })
    }
    
    return Promise.resolve(true)
}

/**
 * Create a focus return handler for modals/drawers
 * Stores the previous active element and provides a way to restore focus
 * @returns {Object} - Object with saveFocus and restoreFocus methods
 */
export const createFocusReturn = () => {
    let previousElement = null
    
    return {
        /**
         * Save the currently focused element
         */
        saveFocus: () => {
            try {
                previousElement = document.activeElement
            } catch (err) {
                previousElement = null
            }
        },
        
        /**
         * Restore focus to the previously saved element
         */
        restoreFocus: () => {
            requestAnimationFrame(() => {
                if (previousElement && isFocusable(previousElement)) {
                    safeFocus(previousElement)
                }
                previousElement = null
            })
        },
        
        /**
         * Clear the saved element without restoring focus
         */
        clearFocus: () => {
            previousElement = null
        }
    }
}

export default {
    isFocusable,
    safeBlur,
    safeFocus,
    safeBlurAndFocus,
    createFocusReturn
}
